export type RealtimeMessageEvent = {
  conversationId: string;
  messageId: string;
  sessionId: string;
  type: "MESSAGE_CREATED";
};

export function subscribeToRealtime(
  listener: (event: RealtimeMessageEvent) => void,
  options: { reconnectMs?: number; sessionToken: string; url?: string }
): () => void {
  if (
    options.sessionToken.length === 0 ||
    typeof WebSocket === "undefined" ||
    typeof window === "undefined"
  ) {
    return () => undefined;
  }
  const reconnectMs = options.reconnectMs ?? 1_000;
  let closed = false;
  let socket: WebSocket | null = null;
  let timer: number | undefined;
  const connect = (): void => {
    if (closed) return;
    socket = new WebSocket(
      options.url ?? realtimeUrl(),
      `scs-session.${options.sessionToken}`
    );
    socket.addEventListener("message", (event) => {
      const parsed = parseMessageEvent(event.data);
      if (parsed !== null) listener(parsed);
    });
    socket.addEventListener("close", () => {
      if (!closed) timer = window.setTimeout(connect, reconnectMs);
    });
    socket.addEventListener("error", () => socket?.close());
  };
  connect();
  return () => {
    closed = true;
    if (timer !== undefined) window.clearTimeout(timer);
    socket?.close();
  };
}

function realtimeUrl(): string {
  const apiOrigin = import.meta.env.VITE_API_ORIGIN || "/api";
  if (/^https?:\/\//u.test(apiOrigin)) {
    const url = new URL(apiOrigin);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `${url.pathname.replace(/\/$/u, "")}/realtime`;
    return url.toString();
  }
  const path = `${apiOrigin.replace(/\/$/u, "")}/realtime`;
  return `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${path}`;
}

function parseMessageEvent(value: unknown): RealtimeMessageEvent | null {
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value) as {
      payload?: Record<string, unknown>;
      type?: unknown;
    };
    const payload = parsed.payload;
    if (
      parsed.type !== "MESSAGE_CREATED" ||
      payload === undefined ||
      typeof payload["conversationId"] !== "string" ||
      typeof payload["messageId"] !== "string" ||
      typeof payload["sessionId"] !== "string"
    ) {
      return null;
    }
    return {
      conversationId: payload["conversationId"],
      messageId: payload["messageId"],
      sessionId: payload["sessionId"],
      type: "MESSAGE_CREATED"
    };
  } catch {
    return null;
  }
}
